import { Component, Input, OnDestroy, OnInit } from '@angular/core'
import { AbstractControl } from '@angular/forms'
import { PlanType } from 'formwork-planner-lib'
import { Plan } from '../../../models/plan'
import { PlanSettings } from '../../../models/planSettings'
import { PlanSettingsService } from '../../../services/plan-settings.service'
import { ProjectRepository } from '../../../repositories/project.repository'
import { StockRepository } from '../../../repositories/stock.repository'
import { combineLatest, map, Subject, takeUntil } from 'rxjs'
import { Doka360ProjectSelectorService } from '../../../services/doka360-project-selector.service'
import { PlanService } from '../../../services/plan.service'
import { NO_STOCK_SELECTED } from '../../../pages/stock/stock.page'

@Component({
  selector: 'efp-plansettings-form',
  templateUrl: './plansettings-form.component.html',
  styleUrls: ['./plansettings-form.component.scss'],
})
export class PlansettingsFormComponent implements OnInit, OnDestroy {
  @Input() plan?: Plan
  @Input() planSettings?: PlanSettings
  @Input() navLink?: string
  @Input() readOnlyMode = false

  private pageLeave$ = new Subject<void>()

  readonly planType = PlanType
  NO_STOCK_SELECTED = NO_STOCK_SELECTED

  get planName(): AbstractControl {
    return this.planSettingsService.planSettingsForm.controls.planName
  }

  get buildType(): AbstractControl {
    return this.planSettingsService.planSettingsForm.controls.buildType
  }

  public filteredStocks$ = combineLatest([
    this.stockRepository.stocks$,
    this.doka360ProjectSelectorService.currentDoka360ProjectUid$,
  ]).pipe(
    takeUntil(this.pageLeave$),
    map(([stocks, currentDoka360ProjectUid]) => {
      return stocks.filter((stock) => {
        return stock.doka360ProjectUid === currentDoka360ProjectUid
      })
    })
  )

  public projects$ = combineLatest([
    this.projectRepository.projects$,
    this.doka360ProjectSelectorService.doka360Project$,
  ]).pipe(
    takeUntil(this.pageLeave$),
    map(([projects, doka360Project]) => {
      if (!doka360Project) {
        return projects
      }

      const project = PlanService.mapModelToProject(doka360Project.projectsModel)
      return [project]
    })
  )

  constructor(
    public readonly planSettingsService: PlanSettingsService,
    public readonly stockRepository: StockRepository,
    public readonly projectRepository: ProjectRepository,
    private readonly doka360ProjectSelectorService: Doka360ProjectSelectorService
  ) {}

  async ngOnInit(): Promise<void> {
    await this.setupSubscriptions()

    this.planSettingsService.planSettingsForm.patchValue({
      planName: this.plan?.name ?? '',
      buildType: this.plan?.buildingType ?? '',
      projectId: this.plan?.projectId ?? 0,
      stockId: this.plan?.stockId ?? NO_STOCK_SELECTED,
    })

    this.planSettingsService.planSettingsForm.get('buildType')?.disable()

    if (!!this.plan?.doka360ProjectUid) {
      this.planSettingsService.planSettingsForm.get('projectId')?.disable()
    }

    if (this.readOnlyMode) {
      this.planSettingsService.planSettingsForm.get('planName')?.disable()
      this.planSettingsService.planSettingsForm.get('projectId')?.disable()
      this.planSettingsService.planSettingsForm.get('stockId')?.disable()
    }
  }

  async ngOnDestroy(): Promise<void> {
    this.pageLeave$.next()

    if (this.readOnlyMode) {
      this.planSettingsService.planSettingsForm.get('planName')?.enable()
      this.planSettingsService.planSettingsForm.get('projectId')?.enable()
      this.planSettingsService.planSettingsForm.get('stockId')?.enable()
    }
  }

  private async setupSubscriptions(): Promise<void> {
    this.pageLeave$.next()
    this.pageLeave$ = new Subject<void>()

    this.planSettingsService.planSettingsForm.controls.projectId.valueChanges
      .pipe(takeUntil(this.pageLeave$))
      .subscribe((projectId) => {
        if (this.plan && projectId !== undefined) {
          this.plan.projectId = projectId
        }
      })

    this.planSettingsService.planSettingsForm.controls.stockId.valueChanges
      .pipe(takeUntil(this.pageLeave$))
      .subscribe((stockId) => {
        if (this.plan) {
          if (stockId === NO_STOCK_SELECTED) this.plan.stockId = null
          else this.plan.stockId = stockId ?? null
        }
      })

    void this.stockRepository.fetchAllByScopeSelector()
    void this.projectRepository.fetchAllStandaloneProjects(false)
  }

  changeName(): void {
    if (this.planName?.value && this.plan) {
      this.plan.name = this.planName.value
    }
  }
}
